import useAssistantChat from 'ai-assistant/hooks/useAssistantChat';
import { AssistantChatStatus } from 'ai-assistant/hooks/useAssistantChat';
import ReactGA from "react-ga4";

import './AssistantChatContent.light.scss';
import './AssistantChatContent.dark.scss';
import { Icon } from '@client:common/icon';
import { AssistantMessage } from '../assistant-message/AssistantMessage';
import { useRef } from 'react';
import { motion, LayoutGroup } from 'framer-motion';

interface AssistantChatContentProps {
  chat: ReturnType<typeof useAssistantChat>;
}

export function AssistantChatContent({ chat }: AssistantChatContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (contentRef.current) {
      const scrollContainer = contentRef.current;
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {

    e.preventDefault();

    if (chat.input.trim() === '') return;

    scrollToBottom();

    chat.coordinator.withStagedUserMessage(chat.input, async newMessageId => {
      chat.handleSubmit(newMessageId);
    })
    ReactGA.event({
      "category": "AI Assistant",
      action: "sent_ai_chat_message",
      label: `Message length: ${chat.input}`,
      value: chat.input.length
    })
  }

  return (
    <LayoutGroup>
      <div className='assistant-chat-container'>
        <div className='assistant-chat-container__content' ref={contentRef}>
          {chat.messages.map((message, index) => (
            <AssistantMessage
              key={message.id}
              message={message}
              chat={chat}
              isMostRecentMessage={index === chat.messages.length - 1}
            />
          ))}
        </div>

        <form className='assistant-chat-container__input-container' onSubmit={handleSubmit}>
          <input
            autoFocus
            className='assistant-chat-container__input'
            placeholder="Ask AI Assistant a question"
            type='text'
            value={chat.input}
            onChange={chat.handleInputChange}
            style={{
              color: chat.coordinator.stagedUserMessageId ? 'transparent' : undefined,
            }}
          />
          <button
            type="submit"
            className='assistant-chat-container__input-container__button'
            disabled={chat.status === AssistantChatStatus.SUBMITTED || chat.status === AssistantChatStatus.STREAMING}
            style={{
              opacity: (chat.status === AssistantChatStatus.SUBMITTED || chat.status === AssistantChatStatus.STREAMING) ? 0.5 : 1
            }}
          >
            <Icon
              icon='send'
              size='24px'
            />
          </button>

          {chat.coordinator.stagedUserMessageId && (
            <motion.div
              layoutId={`message-container-${chat.coordinator.stagedUserMessageId}`}
              className='assistant-chat-container__input-container__animation'
              initial={{
                width: "100%",
                opacity: 0.3,
              }}
              animate={{
                width: 'fit-content',
                translateY: "-110%",
                translateX: "-10%",
                scale: 0.7,
                opacity: 0.5,
              }}
              transition={{
                duration: 0.3,
                ease: 'easeIn'
              }}
              onAnimationComplete={chat.coordinator.handleUserMessageDidFinishStagingAnimation}
            >
              <motion.p
                className='assistant-chat-container__input-container__animation-text'
              >
                {chat.coordinator.stagedUserMessageContent}
              </motion.p>
            </motion.div>
          )}
        </form>
      </div>
    </LayoutGroup>
  )
}
